import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import useTranslations from "@/shared/lang/useTranslations";

import { resolveWalletOperationDestination, WALLETS_HOME_PATH } from "../utils/walletOperationDestination";
import type { WalletOperationKind } from "../utils/walletOperationLinks";

const isKind = (value: string | undefined): value is WalletOperationKind =>
  value === "payins" || value === "payouts";

interface Resolved {
  key: string;
  to: string;
}

/**
 * `/app/wallets/operations/:kind/:operationId`: opens a payin's or payout's movement in its wallet, so "Ver
 * transacción" lands where the money is (`resolveWalletOperationDestination`). Replaces itself in the
 * history, so the back button returns to where the link was.
 */
export function WalletOperationRedirectPage() {
  const { t } = useTranslations("wallets");
  const { kind, operationId } = useParams();
  const { isInternalUser } = useAuth();
  const valid = isKind(kind) && !!operationId;
  const key = `${kind}:${operationId}`;
  const [resolved, setResolved] = useState<Resolved | null>(null);

  useEffect(() => {
    if (!isKind(kind) || !operationId) return;
    let cancelled = false;
    void resolveWalletOperationDestination(kind, operationId, isInternalUser).then((to) => {
      if (!cancelled) setResolved({ key: `${kind}:${operationId}`, to });
    });
    return () => {
      cancelled = true;
    };
  }, [kind, operationId, isInternalUser]);

  if (!valid) return <Navigate to={WALLETS_HOME_PATH} replace />;
  if (resolved?.key === key) return <Navigate to={resolved.to} replace />;

  return (
    <div
      role="status"
      className="flex min-h-[40vh] items-center justify-center gap-2 text-sm text-muted-foreground"
    >
      <Loader2 className="size-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
      {t("operation_link_opening")}
    </div>
  );
}
